import { Badge, Box, Flex, Heading, Input, InputGroup, InputRightElement, Spinner, Text } from "@chakra-ui/react";
import { CloseIcon, SearchIcon } from "@chakra-ui/icons";
import { createColumnHelper } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import WelcomeSection from "components/WelcomeSection";
import { ReactTable } from "components/ReactTable";
import { useTrackingPayments, type TrackingPayment } from "hooks/useTrackingPayments";

function formatDate(s: string | null | undefined) {
  if (!s) return "—";
  return new Date(s).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export default function TrackingPaymentsPage() {
  const { payments, isLoading, error } = useTrackingPayments();
  const [searchValue, setSearchValue] = useState("");

  const filteredData = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    if (!q) return payments;
    return payments.filter(
      (p) =>
        (p.student_name && p.student_name.toLowerCase().includes(q)) ||
        (p.payer_name && p.payer_name.toLowerCase().includes(q)) ||
        (p.amount && String(p.amount).includes(q))
    );
  }, [payments, searchValue]);

  const columns = useMemo(() => {
    const col = createColumnHelper<TrackingPayment>();
    return [
      col.accessor("student_name", { header: "Élève", cell: (info) => <Text fontSize="sm">{info.getValue() ?? "—"}</Text> }),
      col.accessor("payer_name", { header: "Payeur", cell: (info) => <Text fontSize="sm">{info.getValue()}</Text> }),
      col.accessor("amount", { header: "Montant", cell: (info) => <Text fontWeight="medium">{info.getValue()} FCFA</Text> }),
      col.accessor("payment_method_display", { header: "Méthode", cell: (info) => <Text fontSize="sm">{info.getValue() ?? "—"}</Text> }),
      col.accessor("status_display", {
        header: "Statut",
        cell: (info) => <Badge colorScheme={info.getValue() === "Completed" ? "green" : "orange"}>{info.getValue() ?? info.row.original.status}</Badge>,
      }),
      col.accessor("created_at", { header: "Date", cell: (info) => <Text fontSize="sm">{formatDate(info.getValue())}</Text> }),
    ];
  }, []);

  return (
    <>
      <Flex gap="18px" bg="very_light_gray" flex={1} flexDirection="column" py="20px">
        <WelcomeSection />
        <Flex flexDirection="column" alignItems="start" px={{ base: "20px", sm: "24px" }} minHeight="100vh">
          <Heading size="text2xl" as="h5" fontFamily="Arimo" mb={4}>Paiements</Heading>
          {error && <Box mt={4} p={4} bg="red.50" borderRadius="md"><Text color="red.700">Erreur chargement. Vérifiez l&apos;API Tracking.</Text></Box>}
          <Box mt={4} bg="white" w="100%" borderRadius="10px">
            <Flex p={4}>
              <InputGroup w={{ base: "100%", sm: "300px" }}>
                <Input placeholder="Rechercher (élève, payeur, montant)" value={searchValue} onChange={(e) => setSearchValue(e.target.value)} />
                <InputRightElement><SearchIcon color="gray.400" /></InputRightElement>
              </InputGroup>
            </Flex>
            {isLoading && !filteredData.length ? <Flex justify="center" py={10}><Spinner size="lg" /></Flex> : <ReactTable columns={columns as any} data={filteredData} size="sm" />}
            {!isLoading && !filteredData.length && <Box p={8} textAlign="center"><Text color="gray.600">Aucun paiement.</Text></Box>}
          </Box>
        </Flex>
      </Flex>
    </>
  );
}
